import { useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { getToken } from "@/lib/api";
import { ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Route guard for Super Admin pages.
 * - Anonymous (no token) -> redirected to /login
 * - Authenticated but not admin -> Access Denied screen
 * - Authenticated admin -> children
 *
 * Phase D4. Intentionally English-only; admin is a single-user surface.
 */
export default function RequireAdmin({ children }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const token = getToken();
  if (!token) return <Navigate to="/login" replace />;
  if (!user) {
    return (
      <div className="min-h-screen grid place-items-center text-muted-foreground" data-testid="admin-loading">{t("interface.loading")} </div>
    );
  }
  if (user.role !== "admin") {
    return (
      <div className="min-h-screen grid place-items-center px-4">
        <div className="max-w-md w-full text-center space-y-4" data-testid="admin-access-denied">
          <ShieldAlert className="w-12 h-12 mx-auto text-amber-500" />
          <h1 className="text-2xl font-semibold tracking-tight">{t("interface.denied")} </h1>
          <p className="text-sm text-muted-foreground">{t("interface.adminOnly")} </p>
          <Button asChild variant="outline" className="rounded-full" data-testid="admin-back-to-app">
            <a href="/app">{t("interface.backDashboard")} </a>
          </Button>
        </div>
      </div>
    );
  }
  return children;
}
